import { InteractionType, VALID_INTERACTION_TYPES } from './trackInteractionTypes';
import { buildInteractionEvent, pushToGA4 } from './trackInteraction';

const CLICKABLE_SELECTOR = 'a, button, [role="button"]';

const sanitizeToKebabCase = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .substring(0, 60);

const inferInteractionType = (element: HTMLElement): InteractionType => {
  const href = (element as HTMLAnchorElement).href || element.getAttribute('href') || '';

  if (href.includes('.mn.co') || href.includes('/request-a-call-form')) {
    return 'cta_click';
  }

  if (element.tagName === 'BUTTON' || element.getAttribute('role') === 'button') {
    return 'cta_click';
  }

  return 'link_click';
};

const handleClick = (event: MouseEvent): void => {
  const target = event.target as HTMLElement;
  if (!target) return;

  // Find the nearest clickable element (a, button, or role="button")
  const clickableElement = target.closest(CLICKABLE_SELECTOR) as HTMLElement | null;
  if (!clickableElement) return;

  // Only track if the clickable element is inside a tracked section
  const sectionElement = clickableElement.closest('[data-track-section]') as HTMLElement | null;
  if (!sectionElement) return;

  const section = sectionElement.getAttribute('data-track-section') || 'unknown';

  // Use explicit data-track-id if present (check element and ancestors), otherwise generate from text content
  const id =
    clickableElement.getAttribute('data-track-id') ||
    clickableElement.closest('[data-track-id]')?.getAttribute('data-track-id') ||
    sanitizeToKebabCase(clickableElement.textContent || '') ||
    'unknown';

  // Use explicit data-track-type if present and valid, otherwise infer from element/href
  const typeAttr = clickableElement.getAttribute('data-track-type');
  const type: InteractionType =
    typeAttr && VALID_INTERACTION_TYPES.has(typeAttr)
      ? (typeAttr as InteractionType)
      : inferInteractionType(clickableElement);

  const eventData = buildInteractionEvent(id, section, type, clickableElement);
  pushToGA4(eventData);
};

let isListenerAttached = false;

export const initTrackInteractionListener = (): void => {
  if (typeof document === 'undefined' || isListenerAttached) return;

  document.addEventListener('click', handleClick, true);
  isListenerAttached = true;
};

// Test helper - removes the global listener for clean test teardown
export const __removeTrackInteractionListenerForTesting__ = (): void => {
  if (typeof document === 'undefined') return;
  document.removeEventListener('click', handleClick, true);
  isListenerAttached = false;
};
